<script setup>

    import { deepCopy, getModifiedProperties } from '@/Helpers'
    import { useConfigStore, useCaregiverSingleStore } from '@/Stores'
    import { storeToRefs } from 'pinia'
    import { ref, watch } from 'vue'
    import { ExclamationCircleIcon } from '@heroicons/vue/20/solid'
    import LatteCaregiverCapabilitySelection from './LatteCaregiverCapabilitySelection.vue'

    const emit = defineEmits(['caregiver:update', 'caregiver:cancel-update'])

    const configStore = useConfigStore()
    const caregiverStore = useCaregiverSingleStore()
    const { config, caregiverCriterias } = storeToRefs(configStore)
    const { caregiver } = storeToRefs(caregiverStore)

    const caregiverPropsChange = ref(false)

    const localCaregiver = ref({
        matching_criterias: []
    })

    function handleChange(data) {
        if (Object.keys(data).length > 0) {
            emit('caregiver:update', data)
        } else {
            emit('caregiver:cancel-update', caregiver.value.id)
        }
    }

    const capabilitySelectionRef = ref(null)

    function handleClickForFirstIncomplete() {
        if (capabilitySelectionRef.value) {
            capabilitySelectionRef.value.scrollToFirstIncomplete()
        }
    }

    const incompleteCount = ref(0)
    const statusText = ref(`<span class="font-bold">Tip:</span> Get started by selecting the applicable experience &amp; capability options.`)

    function handleSelectedCriteriaUpdate(totalRequired, completed, notStarted) {
        if (completed === 0 && notStarted === totalRequired) {
            return;
        }
        incompleteCount.value = totalRequired - completed - notStarted

        statusText.value = `Specified <span class="font-bold text-lifeworx-green-600">${completed === totalRequired ? 'all' : completed} ${completed > 1 ? 'capabilities' : 'capability'}</span>`

        if (notStarted > 0) {
            statusText.value += ` with <span class="font-bold text-lifeworx-red-500 italic border-b border-dotted hover:border-solid border-lifeworx-red-500 cursor-pointer">${notStarted} not started</span>`
        }

        if (notStarted > 0 && incompleteCount.value > 0) {
            statusText.value += ` and <span class="font-bold text-lifeworx-red-500 italic border-b border-dotted hover:border-solid border-lifeworx-red-500 cursor-pointer">${incompleteCount.value} incomplete</span>`
        } else if (incompleteCount.value > 0) {
            statusText.value += ` with <span class="font-bold text-lifeworx-red-500 italic border-b border-dotted hover:border-solid border-lifeworx-red-500 cursor-pointer">${incompleteCount.value} incomplete</span>`
        } else if (completed === totalRequired) {
            statusText.value += ` &mdash; <span class="font-bold text-lifeworx-green-600 italic">great job!</span> &nbsp;Be sure to periodically check for updates with the caregiver.`
        }
    }

    watch(() => caregiver.value, (newValue) => {
        localCaregiver.value = deepCopy(newValue)
    }, { immediate: true })

    watch(() => localCaregiver.value, (newValue, oldValue) => {
        if (oldValue === undefined) {
            caregiverPropsChange.value = false;
            return;
        }

        if (oldValue.id === newValue.id) {
            if(caregiverPropsChange.value == false) {
                const diff = getModifiedProperties(
                    JSON.stringify(newValue),
                    JSON.stringify(caregiver.value),
                    [
                        'matching_criterias'
                    ]
                )
                handleChange(diff)
                return;
            }
        }
    }, { deep: true })

</script>

<template>
    <div>
        <div
            id="caregiver_capabilities"
        >
            <LatteCaregiverCapabilitySelection
                ref="capabilitySelectionRef"
                v-model="localCaregiver.matching_criterias"
                :categories="config.matching_criteria_descriptor.categories"
                :criteriaDescriptors="caregiverCriterias"
                :certifications="config.certifications"
                @update:selected-criteria-summary="handleSelectedCriteriaUpdate"
            />
        </div>
        <div class="bg-white flex justify-between border-t border-stone-300 p-6 cursor-default">
            <div
                @click="handleClickForFirstIncomplete"
                class="content-center text-sm"
            >
                <span
                    v-html="statusText"
                />
                <ExclamationCircleIcon
                    v-if="incompleteCount > 0"
                    class="inline text-lifeworx-red-500 h-5 w-5 -mt-1 ml-1 cursor-pointer"
                />
            </div>
        </div>
    </div>
</template>
